import { useQueryClient } from "@tanstack/react-query";
import { decryptData, encryptData } from "../utils/encryption";
import { useMemo } from "react";

type TaskProgress = {
  submit: any;
  correctMarks?: number | null;
  possibleMarks?: number;
  taskTitle: string;
  taskType: string;
  skillCategory?: string;
  taskOrder?: number;
};

//EMPTY CHECKS
function isReorderEmpty(submit: any): boolean {
  return submit.answers.every(
    (answer: any) => answer.sentenceArea.length === 0
  );
}

function isGapFillEmpty(submit: any): boolean {
  return submit.parsedGapText.every((segment: any[]) =>
    segment.every(
      part =>
        typeof part !== "object" || part.input === ""
    )
  );
}

function isDropdownEmpty(submit: any): boolean {
  if (!submit?.answers) return true;

  return Object.values(submit.answers).every(
    (value) => value === "" || value == null
  );
}

function isMultipleEmpty(submit: any): boolean {
  return submit.submission?.length === 0;
}

function isMatchEmpty(submit: any): boolean {
  return submit.matches?.length === 0;
}

function isSpellEmpty(submit: any): boolean {
  if (!submit?.submittedInputs) return true;

  return submit.submittedInputs.every(
    (value: string) => value.trim() === ""
  );
}

function isCategoriesEmpty(submit: any): boolean {
  if (!submit?.categoryObjects) return true;

  return submit.categoryObjects.every(
    (category: any) => category.items.length === 0
  );
}

function isReorderSetEmpty(submit: any): boolean {
  return submit.reorderedSets?.every(
    (set: any) => set.length === 0
  );
}

function isBoxtickEmpty(submit: any): boolean {
  if (!submit?.boxtickGuesses) return true;

  return submit.boxtickGuesses.every(
    (guess: any) => guess.isTicked === false
  );
}

function isPhraseScrambleEmpty(submit: any): boolean {
  if (!submit?.gaps) return true;

  return submit.gaps.every((row: any[]) =>
    row.every((gap: any) => !gap.word || gap.word.trim() === "")
  );
}

function isDiagramEmpty(submit: any): boolean {
  if (!submit?.gaps) return true;

  return submit.gaps.every((gapArray: any[]) => gapArray.length === 0);
}

function isOpenEndedEmpty(submit: any): boolean {
  return submit.studentAnswers.length === 0 ||
    submit.studentAnswers.every(
      (a: string) => a.trim() === ""
    );
}


type EmptyCheck = (submit: any) => boolean;

const emptyCheckers: Record<string, EmptyCheck> = {
  reorder: isReorderEmpty,
  gapfill: isGapFillEmpty,
  dropdown: isDropdownEmpty,
  multiple: isMultipleEmpty,
  match: isMatchEmpty,
  spell: isSpellEmpty,
  categories: isCategoriesEmpty,
  reorderset: isReorderSetEmpty,
  boxtick: isBoxtickEmpty,
  phrasescramble: isPhraseScrambleEmpty,
  diagram: isDiagramEmpty,
  openended: isOpenEndedEmpty,
};

export function findEmptyQuestions(
  testProgress: Record<string, TaskProgress>
) {
  return Object.entries(testProgress)
    .filter(([_, question]) => {
      const { taskType, submit } = question;

      const checker = emptyCheckers[taskType];
      if (!checker) return false;

      if (!submit) return true;

      return checker(submit);
    })
    .map(([questionId, question]) => ({
      questionId,
      taskTitle: question.taskTitle,
      taskType: question.taskType,
    }));
}

export const useTestProgress = (
  isTest: boolean,
  lessonId: number,
  attempt: number,
  lessonPlanParts?: any[]
) => {
  const queryClient = useQueryClient();
  const queryKey = ['testProgress', lessonId, attempt];

  // --- Calculate possible marks for a task ---
  const getPossibleMarksFromTask = (task: any): number => {
    const { taskType, taskContent, taskSettings } = task;
    switch (taskType) {
      case 'reorder': return taskContent?.items?.length ?? 0;
      case 'gapfill': return taskContent?.gapWords?.length ?? 0;
      case 'dropdown': return taskContent?.dropdowns?.length ?? 0;
      case 'multiple': return taskContent?.questions?.length ?? 0;
      case 'match': return taskContent?.pairs?.length ?? 0;
      case 'spell': return taskContent?.items?.length ?? 0;
      case 'categories':
        return taskContent?.categoryObjects?.reduce((sum: number, cat: any) => sum + (cat.items?.length ?? 0), 0) ?? 0;
      case 'reorderset': return taskContent?.items?.length ?? 0;
      case 'boxtick': return taskContent?.boxtickItems?.length ?? 0;
      case 'phrasescramble': return taskContent?.items?.length ?? 0;
      case 'diagram': return taskContent?.numberLabels?.length ?? 0;
      case 'openended': return taskContent?.questions?.length ?? 0;
      case 'writing':
      case 'speaking': return taskSettings?.totalPossibleMarks ?? 0;
      default: return 0;
    }
  };

  // --- Initialize progress from lesson plan ---
  const initializeTaskProgress = (): Record<string, TaskProgress> => {
    if (!lessonPlanParts) return {};
    const initialProgress: Record<string, TaskProgress> = {};
    lessonPlanParts.forEach(task => {
      initialProgress[task.taskId.toString()] = {
        submit: null,
        correctMarks: task.taskType === 'writing' || task.taskType === 'speaking' ? null : 0,
        possibleMarks: getPossibleMarksFromTask(task),
        taskTitle: String(task.taskTitle),
        taskType: String(task.taskType),
        skillCategory: task.skillCategory,
        taskOrder: task.taskOrder,
      };
    });
    return initialProgress;
  };

  // --- Hydrate from localStorage if present ---
  const hydrateFromLocalStorage = (): Record<string, TaskProgress> | null => {
    try {
      const saved = localStorage.getItem(`testProgress_${lessonId}_${attempt}`);
      if (!saved) return null;
      return decryptData(saved) as Record<string, TaskProgress>;
    } catch (e) {
      console.warn('Failed to decrypt or parse saved test progress:', e);
      return null;
    }
  };

  // --- Set initial progress ---
  const setInitialProgress = (): Record<string, TaskProgress> => {
    if (!isTest || !lessonId) return {};
    let progress = queryClient.getQueryData<Record<string, TaskProgress>>(queryKey);
    
    if (!progress || Object.keys(progress).length === 0) {
      // Try loading from localStorage
      progress = hydrateFromLocalStorage() || initializeTaskProgress();
      
      queryClient.setQueryData(queryKey, progress);
      queryClient.setQueryDefaults(queryKey, {
        staleTime: 24 * 60 * 60 * 1000,
        gcTime: 24 * 60 * 60 * 1000,
      });
      // Persist to localStorage
      localStorage.setItem(`testProgress_${lessonId}_${attempt}`, encryptData(progress));
    }

    return progress;
  };

  // --- Update progress for a single task ---
  const updateTaskProgress = (taskId: number, progress: TaskProgress) => {
    const taskKey = taskId.toString();
    queryClient.setQueryData<Record<string, TaskProgress>>(queryKey, oldProgress => {
      const updated = {
        ...(oldProgress || {}),
        [taskKey]: {
          ...(oldProgress?.[taskKey] || {}),
          ...progress,
        },
      };
      // Persist to localStorage
      localStorage.setItem(`testProgress_${lessonId}_${attempt}`, encryptData(updated));
      return updated;
    });
  };

  // --- Read current progress ---
  const testProgress = (() => {
    let progress = queryClient.getQueryData<Record<string, TaskProgress>>(queryKey);
    if (!progress || Object.keys(progress).length === 0) {
      // fallback to localStorage
      progress = hydrateFromLocalStorage() || {};
      queryClient.setQueryData(queryKey, progress); // refill cache
    }
    return progress;
  })();

  const emptyQuestions = useMemo(() => {
    return findEmptyQuestions(testProgress);
  }, [testProgress]);

  return { setInitialProgress, updateTaskProgress, testProgress, emptyQuestions };
};